// src/layouts/Layout.tsx
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useInactivityLogout } from "../hooks/useInactivityLogout";
import { useCampaignContext } from "../contexts/CampaignContext";

type LayoutProps = { children: ReactNode };

type Item = { to: string; label: string; icon?: string };
type Group = { label: string; icon?: string; items: Item[]; defaultOpen?: boolean };

const MAIN_ITEMS: Item[] = [
  { to: "/dashboard", label: "Dashboard", icon: "📊" },
  { to: "/log", label: "Quick Log", icon: "📝" },
  // Calendar now serves create/edit/view Events; the separate /events link is removed
  { to: "/calendar", label: "Calendar", icon: "🗓️" },
  { to: "/contacts", label: "Campaign Directory", icon: "👥" },
  { to: "/voters-simple", label: "Voters", icon: "🗳️" },
  { to: "/voters/map", label: "Voter Map", icon: "🗺️" },
];

// Collapsible groups
const IMPORTS_GROUP: Group = {
  label: "Imports",
  icon: "⬆️",
  defaultOpen: true,
  items: [
    { to: "/imports/voters", label: "Import Voters" },
    { to: "/imports/history", label: "Import Voter History" }, // formerly Import History
    { to: "/profiles", label: "Import Profiles" }, // formerly Profiles
  ],
};

const SETTINGS_GROUP: Group = {
  label: "Settings",
  icon: "⚙️",
  defaultOpen: true,
  items: [
    { to: "/account/profile", label: "Account Profile" },
    { to: "/campaigns", label: "Campaigns" },
    { to: "/settings/users", label: "Users" },
    { to: "/settings/contacts", label: "Contact Types" },
    { to: "/settings/relationships", label: "Relationship Types" },
  ],
};

const sidebarWidth = 240;
const topbarH = 52;

function SignOutButton() {
  const navigate = useNavigate();
  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate("/signin", { replace: true });
  }
  return (
    <button
      onClick={handleSignOut}
      style={{
        height: 32,
        padding: "0 12px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.35)",
        background: "rgba(255,255,255,0.06)",
        color: "white",
        cursor: "pointer",
        backdropFilter: "blur(2px)",
      }}
    >
      Sign out
    </button>
  );
}

function AppTitle() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate("/dashboard")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: "transparent",
        border: "none",
        color: "white",
        fontSize: 16,
        fontWeight: 800,
        cursor: "pointer",
      }}
      aria-label="Go to dashboard"
      title="GroundCampaign"
    >
      <span style={{ fontSize: 18 }}>🧭</span>
      GroundCampaign
    </button>
  );
}

function NavItem({ to, label, icon }: Item) {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 8,
        textDecoration: "none",
        color: isActive ? "#0B2E5B" : "#111827",
        background: isActive ? "#e8eef9" : "transparent",
        fontWeight: isActive ? 700 : 500,
      }}
    >
      <span style={{ width: 20, textAlign: "center" }}>{icon ?? "•"}</span>
      <span>{label}</span>
    </NavLink>
  );
}

function CollapsibleGroup({ label, icon, items, defaultOpen = true }: Group) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "8px 10px",
          background: "transparent",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          color: "#111827",
        }}
        aria-expanded={open}
        aria-controls={`group-${label}`}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 20, textAlign: "center" }}>{icon ?? ""}</span>
          <span style={{ fontWeight: 700 }}>{label}</span>
        </div>
        <span aria-hidden="true" style={{ opacity: 0.6 }}>
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div id={`group-${label}`} style={{ display: "grid", gap: 4, paddingLeft: 6 }}>
          {items.map((it) => (
            <NavItem key={it.to} {...it} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavList() {
  return (
    <div>
      <div style={{ display: "grid", gap: 4 }}>
        {MAIN_ITEMS.map((it) => (
          <NavItem key={it.to} {...it} />
        ))}
      </div>

      {/* Collapsible groups */}
      <CollapsibleGroup {...IMPORTS_GROUP} />
      <CollapsibleGroup {...SETTINGS_GROUP} />
    </div>
  );
}

function useIsMobile(bp = 1024) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < bp : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bp]);
  return isMobile;
}

/* ----------------------- Campaign Switcher ----------------------- */

type Campaign = { id: string; name: string };
const LAST = "gc:lastSelectedCampaignId";

async function ladderFetchCampaigns(): Promise<{ list: Campaign[]; mode: number }> {
  // mode 1: campaigns!inner(campaign_users) + invitation_status filter (no user filter)
  {
    const { data, error } = await supabase
      .from("campaigns")
      .select("id,name,campaign_users!inner(user_id,invitation_status)")
      .eq("campaign_users.invitation_status", "accepted");
    if (!error) {
      const rows = (data ?? []) as any[];
      if (rows.length) {
        const list = rows.map((r) => ({ id: r.id, name: r.name }));
        return { list, mode: 1 };
      }
    }
  }

  // get current user id once
  const { data: u } = await supabase.auth.getUser();
  const uid = u?.user?.id || null;

  // mode 2: same join + explicit user filter
  if (uid) {
    const { data, error } = await supabase
      .from("campaigns")
      .select("id,name,campaign_users!inner(user_id,invitation_status)")
      .eq("campaign_users.invitation_status", "accepted")
      .eq("campaign_users.user_id", uid);
    if (!error) {
      const rows = (data ?? []) as any[];
      if (rows.length) {
        const list = rows.map((r) => ({ id: r.id, name: r.name }));
        return { list, mode: 2 };
      }
    }
  }

  // mode 3: two-step (ids from campaign_users → campaigns.in)
  {
    const { data: cu, error: cuErr } = await supabase
      .from("campaign_users")
      .select("campaign_id")
      .eq("invitation_status", "accepted");
    if (!cuErr) {
      const ids = Array.from(new Set((cu ?? []).map((r: any) => r.campaign_id).filter(Boolean)));
      if (ids.length) {
        const { data: cs, error: cErr } = await supabase
          .from("campaigns")
          .select("id,name")
          .in("id", ids);
        if (!cErr && (cs ?? []).length) {
          const list = (cs ?? []) as Campaign[];
          return { list, mode: 3 };
        }
      }
    }
  }

  // mode 4: last resort — rely purely on RLS on campaigns
  {
    const { data, error } = await supabase
      .from("campaigns")
      .select("id,name")
      .order("name", { ascending: true })
      .limit(50);
    if (!error && (data ?? []).length) {
      const list = (data ?? []) as Campaign[];
      return { list, mode: 4 };
    }
  }

  return { list: [], mode: 0 };
}

function CampaignSwitcher() {
  const { campaigns: ctxCampaigns, campaign, setCampaign, loading, error } = useCampaignContext();
  const [hydrated, setHydrated] = useState<Campaign[] | null>(null); // null = not fetched yet
  const [mode, setMode] = useState<number>(-1);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { list, mode } = await ladderFetchCampaigns();
        setHydrated(list.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        setMode(mode);

        if (!campaign && list.length > 0) {
          let pick: Campaign | null = null;
          try {
            const last = localStorage.getItem(LAST);
            pick = list.find((c) => c.id === last) ?? (list.length === 1 ? list[0] : null);
          } catch {}
          if (pick) setCampaign(pick);
        }
      } catch {
        setHydrated([]);
        setMode(0);
      }
    })();
  }, []); // run once

  const source = ctxCampaigns && ctxCampaigns.length > 0 ? ctxCampaigns : hydrated ?? [];
  const showLoading = (loading || hydrated === null) && source.length === 0;

  const DebugChip = () => (
    <span
      style={{
        fontSize: 11,
        padding: "2px 6px",
        borderRadius: 6,
        border: "1px solid rgba(255,255,255,0.35)",
        background: "rgba(255,255,255,0.06)",
        color: "white",
        opacity: 0.8,
        marginRight: 6,
      }}
      title="mode: fetch strategy (1..4); len: campaigns in use"
    >
      mode:{mode} len:{source.length}
    </span>
  );

  if (showLoading) return (<><DebugChip /><span style={{ opacity: 0.8 }}>Loading…</span></>);
  if (error) return (<><DebugChip /><span title={error} style={{ opacity: 0.9 }}>Campaigns</span></>);

  if (!source || source.length === 0) {
    return (
      <>
        <DebugChip />
        <button
          onClick={() => navigate("/campaigns")}
          style={{
            height: 32,
            padding: "0 12px",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(255,255,255,0.06)",
            color: "white",
            cursor: "pointer",
            backdropFilter: "blur(2px)",
            fontWeight: 700,
          }}
          title="Create or join a campaign"
        >
          Create / Join
        </button>
      </>
    );
  }

  if (source.length === 1) {
    return (
      <>
        <DebugChip />
        <span style={{ opacity: 0.9, fontWeight: 700 }}>{source[0].name}</span>
      </>
    );
  }

  return (
    <>
      <DebugChip />
      <select
        value={campaign?.id ?? ""}
        onChange={(e) => {
          const next = source.find((c) => c.id === e.target.value) ?? null;
          setCampaign(next);
        }}
        style={{
          height: 32,
          padding: "0 10px",
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.35)",
          background: "#ffffff",
          color: "#111827",
          cursor: "pointer",
          fontWeight: 700,
        }}
        title="Switch campaign"
      >
        <option value="" disabled>
          Select campaign…
        </option>
        {source.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </>
  );
}

/* --------------------- Layout component --------------------- */

export default function Layout({ children }: LayoutProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useInactivityLogout();

  return (
    <div style={{ minHeight: "100vh", background: "#f3f7fb" }}>
      {/* Topbar */}
      <div
        data-topbar
        style={{
          position: "fixed",
          inset: "0 0 auto 0",
          height: topbarH,
          background: "#0B2E5B",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 14px",
          zIndex: 30,
          boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
        }}
      >
        {/* Mobile menu toggle */}
        <button
          aria-label="Open navigation"
          onClick={() => setOpen((v) => !v)}
          style={{
            display: isMobile ? "inline-flex" : "none",
            width: 36,
            height: 36,
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid rgba(255,255,255,0.35)",
            borderRadius: 8,
            background: "rgba(255,255,255,0.06)",
            color: "white",
            cursor: "pointer",
          }}
        >
          ☰
        </button>

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          aria-label="Go back"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            padding: "0 10px",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(255,255,255,0.06)",
            color: "white",
            cursor: "pointer",
            backdropFilter: "blur(2px)",
          }}
        >
          ← Back
        </button>

        {/* App title */}
        <div style={{ marginLeft: 4 }}>
          <AppTitle />
        </div>

        {/* Right side actions */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <CampaignSwitcher />
          <SignOutButton />
        </div>
      </div>

      {/* Sidebar (desktop) */}
      <aside
        style={{
          position: "fixed",
          top: topbarH,
          left: 0,
          bottom: 0,
          width: isMobile ? 0 : sidebarWidth,
          background: "#ffffff",
          borderRight: "1px solid #e5e7eb",
          padding: isMobile ? 0 : 12,
          overflowY: "auto",
        }}
      >
        {!isMobile && <NavList />}
      </aside>

      {/* Drawer (mobile) */}
      {isMobile && open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 40,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: topbarH,
              left: 0,
              bottom: 0,
              width: 280,
              background: "#ffffff",
              padding: 12,
              overflowY: "auto",
              boxShadow: "2px 0 6px rgba(0,0,0,0.2)",
            }}
          >
            <NavList />
          </div>
        </div>
      )}

      {/* Main content */}
      <main
        data-main
        style={{
          position: "relative",
          marginLeft: isMobile ? 0 : sidebarWidth,
          padding: 16,
          paddingTop: topbarH + 12,
          minHeight: "100vh",
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>{children}</div>
      </main>
    </div>
  );
}
